import React, { useState, useEffect, useCallback, createContext, useContext, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CryptoJS from 'crypto-js';
import { Capacitor } from '@capacitor/core';
import { ComboBox } from "./components/ComboBox";
import { Users, Heart, User, Users2, UserCheck, Shield, CreditCard, FileText, ChevronRight, Lock, Briefcase, Settings, Key, Activity, Zap } from "lucide-react";

// ============================================================================
// LAZY-LOADED PAGE COMPONENTS (Code Splitting for Better Performance)
// ============================================================================
// Admin Pages
const AdminLoginPage = lazy(() => import("./Pages").then(module => ({ default: module.AdminLoginPage })));
const AdminDashboard = lazy(() => import("./Pages").then(module => ({ default: module.AdminDashboard })));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications")); // Has default export
const AdminBillingSettings = lazy(() => import("./pages/AdminBillingSettings")); // Has default export

// Nominee Pages
const NomineeLoginPage = lazy(() => import("./features/nominee/NomineeLoginPage"));
const NomineeDashboard = lazy(() => import("./features/nominee/NomineeDashboard"));
const NomineeVaultPage = lazy(() => import("./features/nominee/NomineeVaultPage"));
const NomineeSettingsPage = lazy(() => import("./features/nominee/NomineeSettingsPage"));
const NomineeActivationPage = lazy(() => import("./features/nominee/NomineeActivationPage"));

// User Data Pages
const CredentialsPage = lazy(() => import("./pages/CredentialsPage").then(module => ({ default: module.CredentialsPage })));
const CardsPage = lazy(() => import("./pages/CardsPage").then(module => ({ default: module.CardsPage })));
const FinancialPage = lazy(() => import("./pages/FinancialPage").then(module => ({ default: module.FinancialPage })));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage").then(module => ({ default: module.DocumentsPage })));
const PoliciesPage = lazy(() => import("./pages/PoliciesPage").then(module => ({ default: module.PoliciesPage })));
const NotesPage = lazy(() => import("./pages/NotesPage").then(module => ({ default: module.NotesPage })));

// Other Pages
const DashboardLayout = lazy(() => import("./DataPages").then(module => ({ default: module.DashboardLayout })));
const SettingsPage = lazy(() => import("./SettingsPage").then(module => ({ default: module.SettingsPage })));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const ManageNomineesPage = lazy(() => import("./pages/ManageNomineesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter").then(module => ({ default: module.NotificationCenter })));
const AndroidLanding = lazy(() => import("./pages/AndroidLanding"));
const DesignPreview = lazy(() => import("./components/DesignPreview"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings").then(module => ({ default: module.NotificationSettings })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(module => ({ default: module.PrivacyPolicy })));
// ============================================================================
import NotificationToastContainer from "./components/NotificationToast";
import FreePromoBanner from "./components/FreePromoBanner";
import VisibilitySelector from "./components/VisibilitySelector";
import ForgotPassword from "./components/ForgotPassword";
import notificationService from "./utils/notificationService";
import ConfirmDialog from "./components/ConfirmDialog";
import FuturisticLoader from "./components/FuturisticLoader";
import LoadingSpinner from "./components/LoadingSpinner";
import MFAVerification from "./components/MFAVerification";
import ErrorBoundary from "./components/ErrorBoundary";
import RoleSwitchModal from "./components/RoleSwitchModal";
import PullToRefresh from "./components/PullToRefresh";
import { SessionProvider } from "./contexts/SessionContext";
import { AdminSessionProvider } from "./contexts/AdminSessionContext";
import { NomineeSessionProvider } from "./contexts/NomineeSessionContext";
import { SESSION_TYPES, clearRoleSession } from "./utils/sessionManager";
import { clearNomineeCache } from "./utils/nomineeCache";
import { useLoginSessionCheck } from "./hooks/useLoginSessionCheck";
import { deriveAuthKeyAsync, deriveKEKAsync, generateDEK, wrapDEKAsync, unwrapDEKAsync, storeMasterKey, clearMasterKey } from "./utils/zkEncryption";
import { clearListCache } from "./utils/listCache";
import { encryptJSONAsync, decryptJSONAsync } from "./utils/zkDataEncryption";
import errorHandler from './utils/errorHandler';
import { validateEncryptionSetup } from "./utils/encryptedAPI";
import apiClient from "./utils/apiConfig";
import logger from "./utils/logger";
import { startTokenRefresh, stopTokenRefresh } from "./utils/tokenRefresh";
import { loadSubscriptionConfig, refreshSubscriptionConfig } from "./utils/subscriptionConfig";
import { AndroidBackHandler } from "./hooks/useAndroidBackButton";
import { useAppLifecycle } from "./hooks/useAppLifecycle";
import { App as CapacitorApp } from '@capacitor/app';


const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Platform detection helper - returns true only when running as Android APK
const isNativeAndroid = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
};

// Yield one frame so React paints the loader before heavy key derivation.
// requestAnimationFrame never fires when the page isn't painting (backgrounded
// tab, throttled WebView, headless render), which would otherwise hang the
// login/signup submit forever. Race it against a timeout so we always continue.
const yieldToPaint = () =>
  new Promise((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    const t = setTimeout(finish, 50);
    requestAnimationFrame(() => { clearTimeout(t); finish(); });
  });

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped when the subscription config fetch resolves so the tree re-renders
  // with real limits/billing mode instead of the boot-time defaults.
  const [, setSubscriptionConfigReady] = useState(false);

  useEffect(() => {
    // Warm the subscription config cache (billing mode, plans, tier limits)
    loadSubscriptionConfig().then(() => setSubscriptionConfigReady(true));

    // Keep billing config fresh: refetch when the tab regains focus and on a
    // 5-minute interval, so admin changes (e.g. enabling free-for-all) reach
    // open tabs without a reload.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSubscriptionConfig();
    };
    document.addEventListener('visibilitychange', onVisible);
    const configRefreshInterval = setInterval(refreshSubscriptionConfig, 5 * 60 * 1000);

    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    // Only validate encryption setup if user is logged in
    if (token && userData) {
      const encryptionValidation = validateEncryptionSetup();
      if (!encryptionValidation.success) {
        // Log detailed error for debugging (development only)
        logger.error('Encryption setup error:', encryptionValidation.message, encryptionValidation.details);
      } else {
        logger.log('✅ Client-side encryption is properly configured');
      }

      setUser(JSON.parse(userData));
      // Start automatic token refresh
      startTokenRefresh();
    }
    setLoading(false);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(configRefreshInterval);
    };
  }, []);

  // Stable identities (useCallback). An inline `logout` got a new reference every
  // render, which churned every effect that depends on it — most damagingly
  // useAppLifecycle, whose [onLogout] dep caused a post-login re-run that wiped the
  // fresh session and bounced the user back to /signin on the APK.
  const login = useCallback((token, userData) => {
    logger.log('🔐 Login called with token:', token ? token.substring(0, 20) + '...' : 'null');
    // Drop any nominee cache left over from a previous account so the gate
    // re-evaluates against THIS user's real nominee count.
    clearNomineeCache();
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    logger.log('✅ Token saved to localStorage');

    // Verify token was saved
    const savedToken = localStorage.getItem("token");
    logger.log('✅ Token verification after save:', savedToken ? savedToken.substring(0, 20) + '...' : 'null');

    setUser(userData);
    // Start automatic token refresh on login
    startTokenRefresh();
  }, []);

  const logout = useCallback(async () => {
    // Use proper session management to clear all user-related data
    clearRoleSession('user');

    // Clear masterKey from session storage (zero-knowledge security)
    clearMasterKey();

    // Clear decrypted list cache so a follow-up login in the same tab
    // can never see the previous user's data
    clearListCache();

    setUser(null);
    // Stop token refresh on logout
    stopTokenRefresh();
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      <SessionProvider user={user} onLogout={logout}>
        {children}
      </SessionProvider>
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Protected Route
// Gate on the persisted token (the real auth artifact, written synchronously at
// login) rather than only the async `user` context state. On native (APK) the
// post-login navigate can render this route before `setUser` propagates, which
// previously bounced a freshly-logged-in user back to /signin. Mirrors the
// token-based AdminRoute. Child screens already null-guard `user`.
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const hasToken = !!localStorage.getItem('token');

  if (loading && !hasToken) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  return (user || hasToken) ? children : <Navigate to="/signin" />;
};

// Admin Route - requires admin authentication
const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');

  if (!adminToken) {
    return <Navigate to="/admin" />;
  }

  return children;
};

// Landing Page - n8n style
const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const useCases = [
    { role: "Store", action: "Passwords, cards & financial records", icon: "🔐" },
    { role: "Secure", action: "Documents & insurance policies", icon: "📄" },
    { role: "Share", action: "With trusted nominees (Pro: up to 4)", icon: "👥" },
    { role: "Access", action: "Anytime from any device", icon: "📱" },
    { role: "Protect", action: "With end-to-end encryption", icon: "🛡️" }
  ];

  // Auto-rotate use cases
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveUseCase((prev) => (prev + 1) % useCases.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [useCases.length]);

  // Close mobile menu on navigation
  const handleNavigate = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  if (loading) {
    return <LoadingSpinner message="Loading DigitalVault..." />;
  }

  if (user) {
    navigate("/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Navigation - Sticky on desktop only, static on mobile */}
      <nav className="md:fixed md:top-0 md:left-0 md:right-0 z-50 bg-[#0a0a0f] md:bg-[#0a0a0f]/95 md:backdrop-blur-md md:border-b md:border-white/10 safe-area-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 md:py-3">
          {/* Mobile Header - [Logo] [Name] ... [Menu] all in one row */}
          <div className="md:hidden flex flex-row items-center">
            <img src="/logo.png" alt="DigitalVault" className="h-8 w-8 drop-shadow-[0_0_8px_rgba(62,123,250,0.5)]" onClick={() => navigate("/")} />
            <span className="ml-2 text-base font-bold text-white cursor-pointer" onClick={() => navigate("/")}>DigitalVault</span>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`ml-auto p-2 rounded-lg transition-all duration-300 ${mobileMenuOpen
                ? 'bg-[#0A2472]'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
                <img src="/logo.png" alt="DigitalVault" className="h-9 w-9 md:h-10 md:w-10 drop-shadow-[0_0_8px_rgba(62,123,250,0.5)]" />
                <span className="text-lg md:text-xl font-bold text-white">DigitalVault</span>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-300">
                <button onClick={() => navigate("/nominee/login")} className="hover:text-[#14B8A6] transition-colors">
                  Nominee Portal
                </button>
                <button onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">
                  Pricing
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/signin")} className="text-sm text-gray-300 hover:text-white transition-colors px-3 py-2">
                Sign in
              </button>
              <button onClick={() => navigate("/signup")} className="bg-[#0A2472] hover:bg-[#0d2d8a] text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all shadow-[0_4px_20px_rgba(10,36,114,0.5)] hover:shadow-[0_6px_30px_rgba(10,36,114,0.7)]">
                Get Started
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0f] border-t border-white/10 px-4 py-3">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleNavigate("/signup")}
                className="w-full bg-[#0A2472] text-white text-sm font-medium py-2.5 rounded-lg"
              >
                Get Started
              </button>
              <button
                onClick={() => handleNavigate("/signin")}
                className="w-full py-2.5 text-sm text-white bg-white/5 rounded-lg hover:bg-white/10 transition"
              >
                Sign in
              </button>
              <button
                onClick={() => handleNavigate("/nominee/login")}
                className="w-full py-2.5 text-sm text-gray-300 bg-white/5 rounded-lg hover:bg-white/10 transition"
              >
                Nominee Portal
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/pricing"); }}
                className="w-full py-2.5 text-sm text-gray-300 bg-white/5 rounded-lg hover:bg-white/10 transition"
              >
                Pricing
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden md:pt-16">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-pink-900/20"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#0A2472]/30 via-[#0A2472]/10 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-10 relative">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-10 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              <h1 className="text-[1.75rem] leading-tight sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4">
                <span className="text-gray-100">Your Digital Legacy,</span>
                <br />
                <span className="text-[#3E7BFA] drop-shadow-[0_0_30px_rgba(10,36,114,0.8)] [text-shadow:0_0_40px_rgba(62,123,250,0.4)]">Protected Forever</span>
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-400 mb-5 sm:mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Store passwords, cards, documents & more with military-grade encryption.
                Add trusted nominees for emergency access. We can't see your data — only you can.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-5 sm:mb-6 justify-center lg:justify-start">
                <button onClick={() => navigate("/signup")} className="bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-medium px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all hover:scale-105 flex items-center justify-center gap-2 shadow-[0_4px_25px_rgba(10,36,114,0.6)] hover:shadow-[0_8px_40px_rgba(10,36,114,0.8)] text-sm sm:text-base">
                  <span>Get started free</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
                <button onClick={() => navigate("/pricing")} className="border border-[#3E7BFA]/40 hover:border-[#3E7BFA]/70 hover:bg-[#0A2472]/20 text-white font-medium px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all hover:shadow-[0_4px_25px_rgba(10,36,114,0.4)] text-sm sm:text-base">
                  View pricing
                </button>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="whitespace-nowrap">Free forever plan</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="whitespace-nowrap">No credit card</span>
                </div>
              </div>
            </div>

            {/* Right - Interactive Vault Preview */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A2472]/50 to-[#3E7BFA]/35 rounded-2xl lg:rounded-3xl blur-3xl"></div>

              {/* Mobile Version - Very compact */}
              <div className="lg:hidden relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-3 border border-[#3E7BFA]/20 overflow-hidden shadow-[0_0_20px_rgba(10,36,114,0.3)]">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>

                {/* Vault Header - Compact */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded bg-gradient-to-br from-[#3E7BFA] to-[#0A2472] flex items-center justify-center relative">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div className="absolute -top-0.5 -right-0.5 w-1 h-1 bg-green-400 rounded-full"></div>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-[10px]">Your Digital Vault</div>
                      <div className="text-[8px] text-gray-500">Everything in one place</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <div className="w-1 h-1 rounded-full bg-green-400 animate-pulse"></div>
                    <span className="text-[8px] text-green-400">Secure</span>
                  </div>
                </div>

                {/* Category Grid - 2x2 */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <div className="p-2 bg-gradient-to-br from-teal-500/15 to-transparent rounded border border-white/10 flex items-center gap-2">
                    <span className="text-base">🔐</span>
                    <span className="text-[9px] text-white font-medium">Passwords</span>
                  </div>
                  <div className="p-2 bg-gradient-to-br from-pink-500/15 to-transparent rounded border border-white/10 flex items-center gap-2">
                    <span className="text-base">💳</span>
                    <span className="text-[9px] text-white font-medium">Cards</span>
                  </div>
                  <div className="p-2 bg-gradient-to-br from-yellow-500/15 to-transparent rounded border border-white/10 flex items-center gap-2">
                    <span className="text-base">💰</span>
                    <span className="text-[9px] text-white font-medium">Money</span>
                  </div>
                  <div className="p-2 bg-gradient-to-br from-purple-500/15 to-transparent rounded border border-white/10 flex items-center gap-2">
                    <span className="text-base">📝</span>
                    <span className="text-[9px] text-white font-medium">Notes</span>
                  </div>
                </div>

                {/* Emergency Access - Single line */}
                <div className="p-2 bg-gradient-to-r from-[#0A2472]/25 to-[#3E7BFA]/10 rounded border border-[#3E7BFA]/30 mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">👨‍👩‍👧</span>
                    <span className="text-white font-medium text-[9px]">Family Emergency Access</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex -space-x-1">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-br from-[#3E7BFA] to-[#0A2472] border border-gray-800"></div>
                      <div className="w-3 h-3 rounded-full bg-gradient-to-br from-[#5B8DEF] to-[#0A2472] border border-gray-800"></div>
                      <div className="w-3 h-3 rounded-full bg-gradient-to-br from-[#7AA3F5] to-[#3E7BFA] border border-gray-800"></div>
                    </div>
                    <span className="text-[7px] text-gray-500">+2</span>
                  </div>
                </div>

                {/* Quick Stats - Single line */}
                <div className="flex items-center justify-center gap-4 text-center">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold text-white">100%</span>
                    <span className="text-[7px] text-gray-500">Private</span>
                  </div>
                  <div className="w-px h-3 bg-white/10"></div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold text-white">∞</span>
                    <span className="text-[7px] text-gray-500">Storage</span>
                  </div>
                  <div className="w-px h-3 bg-white/10"></div>
                  <div className="flex items-center gap-1">
                    <span className="text-[8px] font-bold text-white">Multi-Nominee</span>
                  </div>
                </div>

                {/* Floating glow */}
                <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-[#0A2472]/60 to-[#3E7BFA]/40 rounded-full blur-xl animate-pulse"></div>
              </div>

              {/* Desktop Version - Full */}
              <div className="hidden lg:block relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-[#3E7BFA]/20 overflow-hidden shadow-[0_0_50px_rgba(10,36,114,0.3)]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>

                {/* Vault Preview Window */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#3E7BFA] to-[#0A2472] flex items-center justify-center relative shadow-[0_4px_20px_rgba(10,36,114,0.7)]">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full border border-gray-800"></div>
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm">Your Digital Vault</div>
                        <div className="text-xs text-gray-500">Everything in one place</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-xs text-green-400">Secure</span>
                    </div>
                  </div>

                  {/* Category Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="group p-3 bg-gradient-to-br from-teal-500/10 to-transparent rounded-lg border border-white/10 hover:border-teal-400/50 transition-all cursor-pointer">
                      <svg className="w-6 h-6 text-teal-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                      </svg>
                      <div className="text-white text-xs font-medium">Passwords</div>
                      <div className="text-gray-500 text-xs">Login safely</div>
                    </div>
                    <div className="group p-3 bg-gradient-to-br from-pink-500/10 to-transparent rounded-lg border border-white/10 hover:border-pink-400/50 transition-all cursor-pointer">
                      <svg className="w-6 h-6 text-pink-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                      </svg>
                      <div className="text-white text-xs font-medium">Cards</div>
                      <div className="text-gray-500 text-xs">Pay securely</div>
                    </div>
                    <div className="group p-3 bg-gradient-to-br from-yellow-500/10 to-transparent rounded-lg border border-white/10 hover:border-yellow-400/50 transition-all cursor-pointer">
                      <svg className="w-6 h-6 text-yellow-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <div className="text-white text-xs font-medium">Financial</div>
                      <div className="text-gray-500 text-xs">Track finances</div>
                    </div>
                    <div className="group p-3 bg-gradient-to-br from-purple-500/10 to-transparent rounded-lg border border-white/10 hover:border-purple-400/50 transition-all cursor-pointer">
                      <svg className="w-6 h-6 text-purple-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                      <div className="text-white text-xs font-medium">Notes</div>
                      <div className="text-gray-500 text-xs">Private memo</div>
                    </div>
                  </div>
                </div>

                {/* Emergency Access Feature */}
                <div className="p-4 bg-gradient-to-r from-[#0A2472]/25 via-[#0A2472]/15 to-[#3E7BFA]/10 rounded-xl border border-[#3E7BFA]/30 mb-4 shadow-[0_4px_30px_rgba(10,36,114,0.25)]">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0A2472]/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-[#3E7BFA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold text-sm mb-1">Family Emergency Access</div>
                      <p className="text-xs text-gray-400 leading-relaxed">Share access with loved ones. They can help when you need it most.</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex -space-x-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#3E7BFA] to-[#0A2472] border-2 border-gray-800 shadow-[0_2px_10px_rgba(10,36,114,0.6)]"></div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#5B8DEF] to-[#0A2472] border-2 border-gray-800 shadow-[0_2px_10px_rgba(10,36,114,0.5)]"></div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7AA3F5] to-[#3E7BFA] border-2 border-gray-800 shadow-[0_2px_10px_rgba(62,123,250,0.5)]"></div>
                        </div>
                        <span className="text-xs text-gray-500">+2 more</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xl font-bold text-white mb-1">100%</div>
                    <div className="text-xs text-gray-500">Private</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xl font-bold text-white mb-1">∞</div>
                    <div className="text-xs text-gray-500">Storage</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-sm font-bold text-white mb-1">Multi-Nominee</div>
                    <div className="text-xs text-gray-500">Support</div>
                  </div>
                </div>

                {/* Floating Lock Animation */}
                <div className="absolute -top-4 -right-4 w-28 h-28 bg-gradient-to-br from-[#0A2472]/60 to-[#3E7BFA]/40 rounded-full blur-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What You Can Do Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Mobile: Vertical stacked list */}
        <div className="sm:hidden space-y-2">
          {useCases.map((useCase, i) => (
            <button
              key={i}
              onClick={() => setActiveUseCase(i)}
              className={`w-full px-3 py-2.5 rounded-lg border transition-all ${activeUseCase === i
                ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/50 text-white'
                : 'bg-white/5 border-white/10 text-gray-400'
                }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base">{useCase.icon}</span>
                <div className="text-left">
                  <div className="font-semibold text-white text-xs">{useCase.role}</div>
                  <div className="text-[#3E7BFA] text-[10px]">{useCase.action}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Desktop: Horizontal wrap */}
        <div className="hidden sm:block">
          <div className="flex flex-wrap gap-3 justify-center">
            {useCases.map((useCase, i) => (
              <button
                key={i}
                onClick={() => setActiveUseCase(i)}
                className={`px-5 py-3 rounded-xl border transition-all ${activeUseCase === i
                  ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/50 text-white'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{useCase.icon}</span>
                  <div className="text-left">
                    <div className="font-semibold text-white text-sm">{useCase.role}</div>
                    <div className="text-[#3E7BFA] text-xs">{useCase.action}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trusted By / Stats Section */}
      <div className="border-y border-white/5 bg-white/[0.02] py-8 sm:py-12 mt-6 sm:mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-xs sm:text-sm mb-6 sm:mb-8">Trusted by users who value their privacy</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 lg:gap-16">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-white">100%</div>
              <div className="text-gray-500 text-xs sm:text-sm">End-to-End Encrypted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-white">∞</div>
              <div className="text-gray-500 text-xs sm:text-sm">Secure Storage</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-xl font-bold text-white">Multi-Nominee</div>
              <div className="text-gray-500 text-xs sm:text-sm">Support</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-white">0</div>
              <div className="text-gray-500 text-xs sm:text-sm">Data We Can See</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Data Types */}
      <div className="py-12 sm:py-16 lg:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
              <span className="text-gray-100">Store everything in</span>
              <br />
              <span className="text-[#3E7BFA] drop-shadow-[0_0_25px_rgba(10,36,114,0.7)] [text-shadow:0_0_30px_rgba(62,123,250,0.3)]">one secure vault</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
              All your digital assets encrypted with 100+ specialized fields for comprehensive management
            </p>
          </div>

          {/* Mobile: Compact cards with descriptions */}
          <div className="sm:hidden grid grid-cols-2 gap-2">
            {[
              { title: "Credentials", desc: "Logins & passwords", iconType: "credentials", color: "from-blue-500/20 to-cyan-500/20", iconColor: "text-cyan-400" },
              { title: "Cards", desc: "Credit & debit", iconType: "cards", color: "from-purple-500/20 to-pink-500/20", iconColor: "text-pink-400" },
              { title: "Financial", desc: "Track finances", iconType: "financial", color: "from-green-500/20 to-emerald-500/20", iconColor: "text-emerald-400" },
              { title: "Documents", desc: "IDs & papers", iconType: "documents", color: "from-[#0A2472]/20 to-yellow-500/20", iconColor: "text-yellow-400" },
              { title: "Policies", desc: "Insurance", iconType: "policies", color: "from-red-500/20 to-pink-500/20", iconColor: "text-red-400" },
              { title: "Notes", desc: "Private memos", iconType: "notes", color: "from-indigo-500/20 to-purple-500/20", iconColor: "text-purple-400" }
            ].map((item, i) => {
              const icons = {
                credentials: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>,
                cards: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>,
                financial: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
                documents: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>,
                policies: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>,
                notes: <svg className={`w-5 h-5 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
              };
              return (
                <div key={i} className={`bg-gradient-to-br ${item.color} p-2.5 rounded-lg border border-white/10`}>
                  <div className="mb-1">{icons[item.iconType]}</div>
                  <h3 className="text-[11px] font-bold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-[9px]">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Desktop: Original cards */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {[
              { title: "Credentials", desc: "Website logins, app passwords, API keys", iconType: "credentials", color: "from-blue-500/20 to-cyan-500/20", iconColor: "text-cyan-400" },
              { title: "Payment Cards", desc: "Credit, debit cards with full details", iconType: "cards", color: "from-purple-500/20 to-pink-500/20", iconColor: "text-pink-400" },
              { title: "Financial Records", desc: "Track investments, loans, assets", iconType: "financial", color: "from-green-500/20 to-emerald-500/20", iconColor: "text-emerald-400" },
              { title: "Documents", desc: "IDs, legal papers, certificates", iconType: "documents", color: "from-[#0A2472]/20 to-yellow-500/20", iconColor: "text-yellow-400" },
              { title: "Insurance Policies", desc: "Life, health, auto insurance details", iconType: "policies", color: "from-red-500/20 to-pink-500/20", iconColor: "text-red-400" },
              { title: "Secure Notes", desc: "Private notes, codes, reminders", iconType: "notes", color: "from-indigo-500/20 to-purple-500/20", iconColor: "text-purple-400" }
            ].map((item, i) => {
              const icons = {
                credentials: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>,
                cards: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>,
                financial: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
                documents: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>,
                policies: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>,
                notes: <svg className={`w-8 h-8 lg:w-10 lg:h-10 ${item.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
              };
              return (
                <div key={i} className={`bg-gradient-to-br ${item.color} p-5 lg:p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all hover:scale-[1.02] group`}>
                  <div className="mb-3 lg:mb-4">{icons[item.iconType]}</div>
                  <h3 className="text-base lg:text-xl font-bold text-white mb-2 group-hover:text-[#3E7BFA] transition-colors">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Nominee System Section */}
      <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div className="lg:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-center lg:text-left">
                <span className="text-gray-100">Emergency access for</span>
                <br />
                <span className="text-[#3E7BFA] drop-shadow-[0_0_25px_rgba(10,36,114,0.7)] [text-shadow:0_0_30px_rgba(62,123,250,0.3)]">your loved ones</span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-sm sm:text-base text-center lg:text-left">
                Designate trusted nominees who can securely access your vault when needed.
                Free plan includes 1 nominee, Pro unlocks up to 4 for complete family security and peace of mind.
              </p>
              <div className="space-y-3 sm:space-y-4 max-w-md lg:mx-0">
                {[
                  { title: "Add Nominees", desc: "Invite family with email verification" },
                  { title: "Granular Control", desc: "Choose what each nominee can see" },
                  { title: "Secure Activation", desc: "OTP verification for every access" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#0A2472]/30 flex items-center justify-center text-[#3E7BFA] font-bold text-xs sm:text-sm shadow-[0_2px_15px_rgba(10,36,114,0.4)] border border-[#3E7BFA]/20 flex-shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <div className="font-medium text-white text-sm sm:text-base">{item.title}</div>
                      <div className="text-gray-500 text-xs sm:text-sm">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side graphic */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A2472]/35 to-[#3E7BFA]/25 rounded-3xl blur-3xl"></div>

              {/* Mobile: Compact 2x2 grid with inline layout */}
              <div className="sm:hidden relative grid grid-cols-2 gap-2">
                <div className="bg-white/5 px-2.5 py-2 rounded-lg border border-white/10 flex items-center gap-2">
                  <div className="text-base">👥</div>
                  <div>
                    <div className="text-white font-medium text-[10px]">Add Nominees</div>
                    <div className="text-gray-500 text-[7px]">Up to 5 members</div>
                  </div>
                </div>
                <div className="bg-white/5 px-2.5 py-2 rounded-lg border border-white/10 flex items-center gap-2">
                  <div className="text-base">🔑</div>
                  <div>
                    <div className="text-white font-medium text-[10px]">Secure Activation</div>
                    <div className="text-gray-500 text-[7px]">Key or questions</div>
                  </div>
                </div>
                <div className="bg-white/5 px-2.5 py-2 rounded-lg border border-white/10 flex items-center gap-2">
                  <div className="text-base">🎯</div>
                  <div>
                    <div className="text-white font-medium text-[10px]">Visibility Control</div>
                    <div className="text-gray-500 text-[7px]">All, Primary, Selected</div>
                  </div>
                </div>
                <div className="bg-white/5 px-2.5 py-2 rounded-lg border border-white/10 flex items-center gap-2">
                  <div className="text-base">🔒</div>
                  <div>
                    <div className="text-white font-medium text-[10px]">OTP Verification</div>
                    <div className="text-gray-500 text-[7px]">Email OTP required</div>
                  </div>
                </div>
              </div>

              {/* Desktop: Original grid */}
              <div className="hidden sm:grid grid-cols-2 gap-3 lg:gap-4 relative">
                <div className="bg-white/5 p-4 lg:p-5 rounded-xl border border-white/10">
                  <div className="text-2xl mb-2">👥</div>
                  <div className="text-white font-medium text-sm">Add Nominees</div>
                  <div className="text-gray-500 text-xs">Up to 4 family members</div>
                </div>
                <div className="bg-white/5 p-4 lg:p-5 rounded-xl border border-white/10">
                  <div className="text-2xl mb-2">🔑</div>
                  <div className="text-white font-medium text-sm">Secure Activation</div>
                  <div className="text-gray-500 text-xs">Invitation key or questions</div>
                </div>
                <div className="bg-white/5 p-4 lg:p-5 rounded-xl border border-white/10">
                  <div className="text-2xl mb-2">🎯</div>
                  <div className="text-white font-medium text-sm">Visibility Control</div>
                  <div className="text-gray-500 text-xs">All, Primary, or Selected</div>
                </div>
                <div className="bg-white/5 p-4 lg:p-5 rounded-xl border border-white/10">
                  <div className="text-2xl mb-2">🔒</div>
                  <div className="text-white font-medium text-sm">OTP Verification</div>
                  <div className="text-gray-500 text-xs">Email OTP required</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
              <span className="text-gray-100">Secure. Private.</span>
              <span className="text-[#3E7BFA] drop-shadow-[0_0_25px_rgba(10,36,114,0.7)] [text-shadow:0_0_30px_rgba(62,123,250,0.3)]"> Yours.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            <div className="bg-white/5 p-5 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl border border-white/10 text-center">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">🔐</div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">End-to-End Encrypted</h3>
              <p className="text-gray-500 text-xs sm:text-sm">Data encrypted before leaving your device</p>
            </div>
            <div className="bg-white/5 p-5 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl border border-white/10 text-center">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">🚫</div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">We Can't See It</h3>
              <p className="text-gray-500 text-xs sm:text-sm">Even we cannot access your information</p>
            </div>
            <div className="bg-white/5 p-5 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl border border-white/10 text-center">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">📱</div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">Multi-Factor Auth</h3>
              <p className="text-gray-500 text-xs sm:text-sm">Email OTP for extra protection</p>
            </div>
          </div>
        </div>
      </div>


      {/* CTA Section */}
      <div className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            <span className="text-gray-100">Secure your digital life</span>
            <br />
            <span className="text-[#3E7BFA] drop-shadow-[0_0_30px_rgba(10,36,114,0.8)] [text-shadow:0_0_40px_rgba(62,123,250,0.4)]">today</span>
          </h2>
          <p className="text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Join users who trust DigitalVault with their most sensitive information.
            Free forever — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button onClick={() => navigate("/signup")} className="bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-medium px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-all hover:scale-105 shadow-[0_4px_25px_rgba(10,36,114,0.6)] hover:shadow-[0_8px_40px_rgba(10,36,114,0.8)] text-sm sm:text-base">
              Get started for free
            </button>
            <button onClick={() => navigate("/nominee/login")} className="border border-gray-600 hover:border-gray-500 text-white font-medium px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-colors text-sm sm:text-base">
              I'm a Nominee
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 sm:py-10 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Footer - Improved layout */}
          <div className="sm:hidden mb-8">
            {/* Brand - Centered on mobile */}
            <div className="text-center mb-5">
              <div className="flex items-center justify-center gap-2 mb-1.5">
                <img src="/logo.png" alt="DigitalVault" className="h-6 w-6 drop-shadow-[0_0_6px_rgba(62,123,250,0.4)]" />
                <span className="font-bold text-white text-sm">DigitalVault</span>
              </div>
              <p className="text-gray-500 text-[10px]">Secure your digital legacy</p>
            </div>

            {/* Product + Security side by side, same row, opposite ends */}
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }} className="mb-4 px-2">
              {/* Product - Left aligned */}
              <div style={{ textAlign: 'left' }}>
                <h4 className="font-medium text-white mb-2 text-xs">Product</h4>
                <ul className="space-y-1 text-[11px] text-gray-400">
                  <li className="hover:text-white cursor-pointer" onClick={() => navigate("/signup")}>Create Account</li>
                  <li className="hover:text-white cursor-pointer" onClick={() => navigate("/signin")}>Sign In</li>
                  <li className="hover:text-white cursor-pointer" onClick={() => navigate("/nominee/login")}>Nominee Portal</li>
                </ul>
              </div>

              {/* Security - Right aligned */}
              <div style={{ textAlign: 'right' }}>
                <h4 className="font-medium text-white mb-2 text-xs">Security</h4>
                <ul className="space-y-1 text-[11px] text-gray-400">
                  <li>End-to-End Encrypted</li>
                  <li>Multi-Factor Auth</li>
                  <li>Privacy Guaranteed</li>
                </ul>
              </div>
            </div>

            {/* Support - Centered */}
            <div className="text-center pt-3 border-t border-white/5">
              <h4 className="font-medium text-white mb-2 text-xs">Support</h4>
              <ul className="space-y-1 text-[11px] text-gray-400">
                <li>support@digitalvault.in</li>
                <li>24/7 Priority (Pro) • Help Center</li>
              </ul>
            </div>
          </div>

          {/* Desktop Footer - 4 column grid */}
          <div className="hidden sm:grid sm:grid-cols-4 gap-8 mb-8 sm:mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <img src="/logo.png" alt="DigitalVault" className="h-7 w-7 sm:h-8 sm:w-8 drop-shadow-[0_0_6px_rgba(62,123,250,0.4)]" />
                <span className="font-bold text-white text-sm sm:text-base">DigitalVault</span>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm">Secure your digital legacy</p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Product</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/signup")}>Create Account</li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/signin")}>Sign In</li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => navigate("/nominee/login")}>Nominee Portal</li>
              </ul>
            </div>

            {/* Security */}
            <div>
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Security</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li>End-to-End Encrypted</li>
                <li>Multi-Factor Auth</li>
                <li>Privacy Guaranteed</li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li>support@digitalvault.in</li>
                <li>24/7 Priority (Pro)</li>
                <li>Help Center</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6 sm:pt-8 text-center">
            <p className="text-gray-500 text-xs sm:text-sm">© 2025 DigitalVault. All rights reserved.</p>
            <p className="text-gray-600 text-[10px] sm:text-xs mt-1.5 sm:mt-2">End-to-end encrypted. Your data stays private — always.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Signup Page
const SignupPage = () => {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobileNumber: ""
  });

  // Redirect to dashboard if user is already logged in (in useEffect, not during render)
  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  // Show loading spinner while checking auth
  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#3E7BFA] border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match on client-side
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Validate password strength (minimum 8 characters)
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    try {
      // KEY WRAPPING FLOW:
      // 1. Derive authKey for authentication
      // 2. Derive KEK (Key Encryption Key) from password
      // 3. Generate random DEK (Data Encryption Key)
      // 4. Wrap DEK with KEK
      // 5. Send authHash + wrappedDEK to server
      // 6. Store DEK (not KEK!) in sessionStorage

      // Yield a frame so React paints the loader before the synchronous,
      // CPU-heavy PBKDF2 derivations block the main thread.
      await new Promise(requestAnimationFrame);

      const authKey = await deriveAuthKeyAsync(formData.password, formData.email);
      const authHash = CryptoJS.SHA256(authKey).toString(CryptoJS.enc.Hex);
      const kek = await deriveKEKAsync(formData.password, formData.email);
      const dek = generateDEK();  // Random 256-bit key
      const wrappedDEK = await wrapDEKAsync(dek, kek);  // Encrypt DEK with KEK

      // NEW: Encrypt mobile number with DEK (if provided)
      let encryptedMobileNumber = null;
      if (formData.mobileNumber) {
        encryptedMobileNumber = await encryptJSONAsync({ value: formData.mobileNumber }, dek);
      }

      const signupData = {
        fullName: formData.fullName,
        email: formData.email,
        authHash: authHash,  // Server will double-hash this for storage
        encryptedMobileNumber: encryptedMobileNumber,  // NEW: Send encrypted mobile
        wrappedDEK: wrappedDEK  // Encrypted DEK (can only be unwrapped with password)
      };

      const response = await apiClient.post(`${API}/auth/signup`, signupData);

      // Guard: response must be a JSON object with success flag (not an HTML SPA fallback)
      if (!response.data || typeof response.data !== 'object' || !response.data.success) {
        throw new Error('Invalid response from server — check VITE_BACKEND_URL configuration');
      }

      // Do NOT auto-login. User must sign in explicitly after registration.
      // Discard the DEK and any issued token; a fresh login re-derives them.
      clearMasterKey();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');

      if (response.data.requiresApproval) {
        // Manual-approval mode: account is pending until an admin approves it.
        toast.success("Account created! An admin must approve your account before you can sign in.");
      } else {
        toast.success("Account created successfully! Please sign in to continue.");
      }
      navigate("/signin");
    } catch (error) {
      errorHandler.handle(error, 'signup', 'Registration failed. Please check your information and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <FuturisticLoader message="Creating Your Account..." />}
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <button
              onClick={() => navigate("/")}
              className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <span>←</span>
              <span className="text-sm">Back to Home</span>
            </button>
          </div>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/logo.png" alt="DigitalVault" className="h-16 w-16 drop-shadow-[0_0_12px_rgba(62,123,250,0.5)]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Create Account</h1>
            <p className="text-gray-400">Join <span className="text-[#3E7BFA]">DigitalVault</span> today</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
            <input
              type="text"
              placeholder="Full Name"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors mb-4"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors mb-4"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <input
              type="tel"
              placeholder="Mobile Number (with country code)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors mb-4"
              value={formData.mobileNumber}
              onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
              required
            />
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password (min 8 characters)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#3E7BFA] transition-colors"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <div className="relative mb-6">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#3E7BFA] transition-colors"
                title={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <button type="submit" className="w-full bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-medium py-3 rounded-lg transition-all shadow-[0_4px_20px_rgba(10,36,114,0.5)] hover:shadow-[0_6px_30px_rgba(10,36,114,0.7)]">
              Sign Up
            </button>
            <p className="text-center mt-4 text-gray-400 text-sm">
              Already have an account? <Link to="/signin" className="text-[#3E7BFA] hover:text-[#5B8DEF] transition">Sign In</Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

// Signin Page
const SigninPage = () => {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [mfaData, setMfaData] = useState({ email: "", tempToken: "" });

  // Pre-submit session check to prevent conflicting logins
  const {
    checkSessionBeforeLogin,
    broadcastLogin,
    showRoleSwitchModal,
    currentRole,
    targetRole,
    handleConfirmSwitch,
    handleCancelSwitch
  } = useLoginSessionCheck(SESSION_TYPES.USER, navigate);

  // Redirect to dashboard if user is already logged in (in useEffect, not during render)
  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  // Show loading spinner while checking auth
  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#3E7BFA] border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // PRE-SUBMIT CHECK: Prevent conflicting logins
    if (!checkSessionBeforeLogin()) {
      return; // Block login - modal will show if needed
    }

    setIsLoading(true);
    try {
      // KEY UNWRAPPING FLOW:
      // 1. Derive authKey for authentication
      // 2. Send authHash to server
      // 3. Server returns wrappedDEK
      // 4. Derive KEK from password
      // 5. Unwrap DEK using KEK
      // 6. Store DEK in sessionStorage

      // Yield a frame so React paints the loader before key derivation.
      await yieldToPaint();

      const authKey = await deriveAuthKeyAsync(formData.password, formData.email);
      const authHash = CryptoJS.SHA256(authKey).toString(CryptoJS.enc.Hex);

      const loginData = {
        email: formData.email,
        authHash: authHash
      };

      const response = await apiClient.post(`${API}/auth/signin`, loginData);

      // Derive KEK from password
      const kek = await deriveKEKAsync(formData.password, formData.email);

      // Unwrap DEK from server response
      const wrappedDEK = response.data.wrappedDEK;
      if (!wrappedDEK) {
        throw new Error('Server did not return encryption key. Please contact support.');
      }

      const dek = await unwrapDEKAsync(wrappedDEK, kek);

      // NEW: Decrypt mobile number if encrypted
      let mobileNumber = "";
      if (response.data.mobileEncrypted && response.data.encryptedMobileNumber) {
        try {
          const decrypted = await decryptJSONAsync(response.data.encryptedMobileNumber, dek);
          mobileNumber = decrypted.value;
        } catch (error) {
          console.error('Failed to decrypt mobile:', error);
          mobileNumber = "";  // Graceful fallback
        }
      } else {
        // Legacy user with plaintext mobile
        mobileNumber = response.data.mobileNumber || "";
      }

      // Update response data with decrypted mobile
      const userData = {
        ...response.data,
        mobileNumber: mobileNumber
      };

      // NEW: Silent migration for legacy users with plaintext mobile
      if (response.data.requiresMobileMigration && response.data.mobileNumber) {
        try {
          const encryptedMobileNumber = await encryptJSONAsync({ value: response.data.mobileNumber }, dek);

          await apiClient.post(`${API}/profile/migrate-mobile`, {
            encryptedMobileNumber
          }, {
            headers: { 'Authorization': `Bearer ${response.data.accessToken}` }
          });

          logger.log('✅ Mobile number migrated to encrypted format');
        } catch (migrationError) {
          // Don't block login if migration fails - will retry next login
          logger.error('Mobile migration failed (non-blocking):', migrationError);
        }
      }

      // Check if MFA is required
      if (response.data.mfa_required) {
        // Store DEK temporarily for after MFA
        sessionStorage.setItem('temp_mk', dek);
        setMfaData({
          email: formData.email,
          tempToken: response.data.temp_token
        });
        setShowMFAVerification(true);
        toast.info(response.data.message || "Verification code sent to your email");
      } else {
        // Normal login without MFA - store DEK and token
        storeMasterKey(dek);  // Function name kept for compatibility, stores DEK

        if (response.data.accessToken) {
          localStorage.setItem("token", response.data.accessToken);
          login(response.data.accessToken, userData);  // Pass userData with decrypted mobile
        }

        // Broadcast new session to other tabs
        broadcastLogin(SESSION_TYPES.USER);

        toast.success("Login successful!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error('Login error:', error);
      // Check if it's a 401 (invalid credentials) or other error
      if (error.response?.status === 401 || error.response?.status === 404) {
        const errorMessage = 'Invalid credentials. Please check your email and password.';
        setError(errorMessage);
        toast.error(errorMessage);
      } else if (error.response?.status === 403) {
        // Account-status blocks from signin (pending approval / rejected /
        // suspended). Show a clear, actionable message rather than "Login failed".
        const detail = (error.response?.data?.detail || '').toLowerCase();
        let errorMessage;
        if (detail.includes('pending')) {
          errorMessage = 'Your account is awaiting admin approval. You\'ll be able to sign in once it\'s approved.';
        } else if (detail.includes('rejected')) {
          errorMessage = 'Your account registration was not approved. Please contact support.';
        } else if (detail.includes('suspended')) {
          errorMessage = 'Your account has been suspended. Please contact support.';
        } else {
          errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
        }
        setError(errorMessage);
        toast.error(errorMessage);
      } else {
        const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASuccess = (userData) => {
    // Retrieve temporarily stored DEK
    const tempDEK = sessionStorage.getItem('temp_mk');
    if (tempDEK) {
      storeMasterKey(tempDEK);  // Function name kept for compatibility, stores DEK
      sessionStorage.removeItem('temp_mk');
    }

    const token = userData.accessToken || userData.token;
    localStorage.setItem('token', token);
    login(token, userData);

    // Broadcast new session to other tabs
    broadcastLogin(SESSION_TYPES.USER);

    setShowMFAVerification(false);
    navigate("/dashboard");
  };

  const handleMFACancel = () => {
    setShowMFAVerification(false);
    setMfaData({ email: "", tempToken: "" });
  };

  return (
    <>
      {isLoading && <FuturisticLoader message="Signing You In..." />}
      {showRoleSwitchModal && (
        <RoleSwitchModal
          currentRole={currentRole}
          targetRole={targetRole}
          onConfirm={handleConfirmSwitch}
          onCancel={handleCancelSwitch}
        />
      )}
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <button
              onClick={() => navigate("/")}
              className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <span>←</span>
              <span className="text-sm">Back to Home</span>
            </button>
          </div>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/logo.png" alt="DigitalVault" className="h-16 w-16 drop-shadow-[0_0_12px_rgba(62,123,250,0.5)]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-gray-400">Sign in to your <span className="text-[#3E7BFA]">vault</span></p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}
            <input
              type="email"
              placeholder="Email"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors mb-4"
              value={formData.email}
              onChange={(e) => { setError(''); setFormData({ ...formData, email: e.target.value }); }}
              required
            />
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
                value={formData.password}
                onChange={(e) => { setError(''); setFormData({ ...formData, password: e.target.value }); }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#3E7BFA] transition-colors"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <div className="text-right mb-6">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-[#3E7BFA] hover:text-[#5B8DEF] text-sm transition"
              >
                Forgot Password?
              </button>
            </div>
            <button type="submit" className="w-full bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-medium py-3 rounded-lg transition-all shadow-[0_4px_20px_rgba(10,36,114,0.5)] hover:shadow-[0_6px_30px_rgba(10,36,114,0.7)]">
              Sign In
            </button>
            <p className="text-center mt-4 text-gray-400 text-sm">
              Don't have an account? <Link to="/signup" className="text-[#3E7BFA] hover:text-[#5B8DEF] transition">Sign Up</Link>
            </p>
          </form>

          {showForgotPassword && (
            <ForgotPassword
              onClose={() => setShowForgotPassword(false)}
              onSuccess={() => {
                toast.info("You can now sign in with your new password");
              }}
            />
          )}

          {showMFAVerification && (
            <MFAVerification
              email={mfaData.email}
              tempToken={mfaData.tempToken}
              onSuccess={handleMFASuccess}
              onCancel={handleMFACancel}
            />
          )}
        </div>
      </div>
    </>
  );
};

// Nominee Modal
const NomineeModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    relationship: "Spouse",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`${API}/nominee/setup`, formData);
      toast.success("Nominee setup successful!");
      onSuccess();
    } catch (error) {
      errorHandler.handle(error, 'nominee-setup', 'Failed to setup nominee. Please try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-start sm:items-center justify-center z-[100] px-6 overflow-y-auto"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
    >
      <div className="bg-[#0a0a0f] border border-white/10 rounded-2xl p-8 max-w-md w-full my-auto max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem)] overflow-y-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Setup Nominee (Required)</h2>
        <p className="text-gray-300 mb-6">Designate a trusted person who can access your vault when needed.</p>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Nominee Full Name"
            className="w-full mb-3 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Nominee Email"
            className="w-full mb-3 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <input
            type="tel"
            placeholder="Nominee Mobile Number"
            className="w-full mb-3 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
            value={formData.mobileNumber}
            onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
            required
          />
          <ComboBox
            label="Relationship"
            options={[
              { value: 'Spouse', label: 'Spouse', icon: <Heart className="w-4 h-4" /> },
              { value: 'Child', label: 'Child', icon: <User className="w-4 h-4" /> },
              { value: 'Parent', label: 'Parent', icon: <Users className="w-4 h-4" /> },
              { value: 'Sibling', label: 'Sibling', icon: <Users2 className="w-4 h-4" /> },
              { value: 'Friend', label: 'Friend', icon: <UserCheck className="w-4 h-4" /> },
              { value: 'Other', label: 'Other', icon: <Users className="w-4 h-4" /> }
            ]}
            value={formData.relationship}
            onChange={(value) => setFormData({ ...formData, relationship: value })}
            placeholder="Select relationship..."
            className="mb-3"
          />
          <textarea
            placeholder="Additional Notes (optional)"
            className="w-full mb-4 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
            rows="3"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <button type="submit" className="w-full py-3 rounded-lg bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-semibold transition">
            Save Nominee
          </button>
        </form>
      </div>
    </div>
  );
};

// Dashboard Page
// Aurora Design Components
import GlassCard from "./components/GlassCard";
import GlassButton from "./components/GlassButton";
// Aurora Components imported at top

// Dashboard Page
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load stats");
    }
    // Note: NotificationBadge component handles all notification fetching and polling
  };

  if (!stats) {
    return (
      <DashboardLayout user={user} logout={logout} navigate={navigate}>
        <div className="flex items-center justify-center h-64">
          {/* Aurora Loader */}
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-transparent border-l-purple-500 border-b-purple-500 animate-spin-slow"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse shadow-[0_0_10px_white]"></div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const dashboardItems = [
    { title: 'Credentials', desc: 'Stored passwords', count: stats.credentials, icon: Key, gradient: 'from-blue-500/20 to-cyan-500/20', accent: 'text-cyan-400', path: "/credentials" },
    { title: 'Cards', desc: 'Payment methods', count: stats.cards, icon: CreditCard, gradient: 'from-purple-500/20 to-pink-500/20', accent: 'text-pink-400', path: "/cards" },
    { title: 'Financial', desc: 'Investments & loans', count: stats.financial, icon: Briefcase, gradient: 'from-yellow-500/20 to-orange-500/20', accent: 'text-yellow-400', path: "/financial" },
    { title: 'Documents', desc: 'Important files', count: stats.documents, icon: FileText, gradient: 'from-emerald-500/20 to-teal-500/20', accent: 'text-teal-400', path: "/documents" },
    { title: 'Policies', desc: 'Insurance details', count: stats.policies, icon: Shield, gradient: 'from-red-500/20 to-pink-500/20', accent: 'text-red-400', path: "/policies" },
    { title: 'Notes', desc: 'Secure memos', count: stats.notes, icon: Lock, gradient: 'from-indigo-500/20 to-violet-500/20', accent: 'text-indigo-400', path: "/notes" },
  ];

  return (
    <DashboardLayout user={user} logout={logout} navigate={navigate}>
      <PullToRefresh onRefresh={fetchStats}>
        <div className="pb-24"> {/* Padding for floating elements */}

          {/* Header Section */}
          <div className="flex justify-between items-center gap-3 mb-6 px-1">
            <div className="flex items-baseline gap-3 min-w-0">
              <p className="text-gray-400 text-xs font-bold tracking-wider uppercase flex-shrink-0">Welcome Back</p>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent truncate min-w-0">
                {user?.fullName?.split(' ')[0] || 'User'}
              </h1>
            </div>
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 p-[2px] shadow-[0_0_15px_rgba(59,130,246,0.5)]">
              {/* User Avatar Placeholder */}
              <div className="w-full h-full rounded-full bg-[#050505] flex items-center justify-center text-lg font-bold text-white">
                {user?.fullName?.charAt(0) || 'U'}
              </div>
            </div>
          </div>



          {/* Quick Actions (Dashboard Grid) */}
          <h2 className="text-xl font-bold text-white mb-4 px-1">Your Assets</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {dashboardItems.map((item, index) => (
              <GlassCard
                key={index}
                className="relative h-[150px] p-4"
                onClick={() => navigate(item.path)}
                gradient={item.gradient}
              >
                <div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${item.gradient} mb-3 shadow-inner`}>
                    <item.icon size={20} className={item.accent} />
                  </div>
                  <h3 className="text-base font-bold text-white leading-tight">{item.title}</h3>
                  <p className="text-gray-400 text-[10px] mt-1">{item.desc}</p>
                </div>

                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl font-bold text-white/90">{item.count}</span>
              </GlassCard>
            ))}
          </div>

          {/* Extra Sections (Nominees & Settings) as wide cards */}


        </div>
      </PullToRefresh>
    </DashboardLayout>
  );
};

// Wrapper components - defined outside to avoid recreation on each render
const CredentialsPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <CredentialsPage user={user} logout={logout} navigate={navigate} />;
};

const CardsPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <CardsPage user={user} logout={logout} navigate={navigate} />;
};

const FinancialPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <FinancialPage user={user} logout={logout} navigate={navigate} />;
};

const DocumentsPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <DocumentsPage user={user} logout={logout} navigate={navigate} />;
};

const PoliciesPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <PoliciesPage user={user} logout={logout} navigate={navigate} />;
};

const NotesPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <NotesPage user={user} logout={logout} navigate={navigate} />;
};

const ManageNomineesPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <ManageNomineesPage user={user} logout={logout} navigate={navigate} />;
};

const SettingsPageWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <SettingsPage DashboardLayout={DashboardLayout} user={user} logout={logout} navigate={navigate} />;
};

const NotificationCenterWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <NotificationCenter user={user} logout={logout} navigate={navigate} />;
};

const NotificationSettingsWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return <NotificationSettings user={user} logout={logout} navigate={navigate} />;
};

export default function App() {
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent 
          showExitConfirm={showExitConfirm} 
          setShowExitConfirm={setShowExitConfirm} 
        />
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppContent({ showExitConfirm, setShowExitConfirm }) {
  const { logout } = useAuth();

  // Handle app lifecycle (kill detection & background timeout)
  useAppLifecycle(logout);

  const handleExitConfirm = async () => {
    await logout();
    setShowExitConfirm(false);
    if (window.Capacitor?.isNativePlatform?.()) {
      CapacitorApp.exitApp();
    }
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <FreePromoBanner />
      <Suspense fallback={<FuturisticLoader message="Loading..." />}>
        <Routes>
          <Route path="/" element={isNativeAndroid() ? <AndroidLanding /> : <LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signin" element={<SigninPage />} />
          <Route path="/design-preview" element={<DesignPreview />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/credentials" element={<ProtectedRoute><CredentialsPageWrapper /></ProtectedRoute>} />
          <Route path="/cards" element={<ProtectedRoute><CardsPageWrapper /></ProtectedRoute>} />
          <Route path="/financial" element={<ProtectedRoute><FinancialPageWrapper /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPageWrapper /></ProtectedRoute>} />
          <Route path="/policies" element={<ProtectedRoute><PoliciesPageWrapper /></ProtectedRoute>} />
          <Route path="/notes" element={<ProtectedRoute><NotesPageWrapper /></ProtectedRoute>} />
          <Route path="/nominees" element={<ProtectedRoute><ManageNomineesPageWrapper /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPageWrapper /></ProtectedRoute>} />
          <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/notification-center" element={<ProtectedRoute><NotificationCenterWrapper /></ProtectedRoute>} />
          <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettingsWrapper /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/admin/notifications" element={<AdminRoute><AdminSessionProvider adminData={{}} onLogout={() => { window.location.href = '/admin'; }}><AdminNotifications user={{}} logout={() => { }} /></AdminSessionProvider></AdminRoute>} />
          <Route path="/admin/billing" element={<AdminRoute><AdminSessionProvider adminData={{}} onLogout={() => { window.location.href = '/admin'; }}><AdminBillingSettings /></AdminSessionProvider></AdminRoute>} />
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminSessionProvider adminData={{}} onLogout={() => { window.location.href = '/admin'; }}><AdminDashboard /></AdminSessionProvider></AdminRoute>} />
          <Route path="/nominee/activate" element={<NomineeActivationPage />} />
          <Route path="/nominee/login" element={<NomineeLoginPage />} />
          <Route path="/nominee/dashboard" element={<NomineeSessionProvider nomineeData={{}} onLogout={() => { window.location.href = '/nominee/login'; }}><NomineeDashboard /></NomineeSessionProvider>} />
          <Route path="/nominee/vault" element={<NomineeSessionProvider nomineeData={{}} onLogout={() => { window.location.href = '/nominee/login'; }}><NomineeVaultPage /></NomineeSessionProvider>} />
          <Route path="/nominee/settings" element={<NomineeSessionProvider nomineeData={{}} onLogout={() => { window.location.href = '/nominee/login'; }}><NomineeSettingsPage /></NomineeSessionProvider>} />
        </Routes>
      </Suspense>
      {/* Android hardware back button handler */}
      <AndroidBackHandler onLogoutRequest={() => setShowExitConfirm(true)} />
      
      {showExitConfirm && (
        <ConfirmDialog
          isOpen={showExitConfirm}
          title="Exit App?"
          message="Are you sure you want to logout and exit the app?"
          confirmText="Logout & Exit"
          cancelText="Cancel"
          danger={true}
          onConfirm={handleExitConfirm}
          onClose={() => setShowExitConfirm(false)}
        />
      )}

      <ToastContainer
        position={isNativeAndroid() ? "bottom-center" : "top-right"}
        theme="dark"
        limit={2}
        autoClose={3000}
        newestOnTop
        closeOnClick
        draggable
        hideProgressBar
      />
      <NotificationToastContainer token={localStorage.getItem('accessToken')} />
    </BrowserRouter>
  );
}
